/*
 * Theme provider — light / dark / system.
 * Toggles the `.dark` class on <html> so Tailwind's class-based dark mode and
 * our scoped `.dark` overrides (index.css) take effect. `system` follows the OS
 * preference live via matchMedia. Choice persisted in localStorage.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'raptor:theme';

const getSystem = (): ResolvedTheme =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const applyClass = (resolved: ResolvedTheme): void => {
  const el = document.documentElement;
  el.classList.toggle('dark', resolved === 'dark');
  el.style.colorScheme = resolved;
};

const readStored = (): ThemeMode => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s === 'light' || s === 'dark' || s === 'system' ? s : 'light';
  } catch { return 'light'; }
};

interface ThemeCtx { mode: ThemeMode; resolved: ResolvedTheme; setMode: (m: ThemeMode) => void }
const Ctx = createContext<ThemeCtx>({ mode: 'system', resolved: 'light', setMode: () => {} });

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(readStored);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => (readStored() === 'system' ? getSystem() : (readStored() as ResolvedTheme)));

  useEffect(() => {
    const compute = (): void => {
      const r: ResolvedTheme = mode === 'system' ? getSystem() : mode;
      setResolved(r);
      applyClass(r);
    };
    compute();
    if (mode === 'system' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (): void => compute();
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [mode]);

  const set = useCallback((m: ThemeMode): void => {
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore */ }
    setMode(m);
  }, []);

  return <Ctx.Provider value={{ mode, resolved, setMode: set }}>{children}</Ctx.Provider>;
};

export const useTheme = (): ThemeCtx => useContext(Ctx);
